import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import RadioButtonChecked from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import {
  Chip,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { plural } from "../../program-access/access-model";
import { ProgramIcon } from "../../programs/components/program-icon";

import type { ProgramNode } from "../../program-tree/program-tree";
import type { ProgramMoveTarget } from "../program-move";
import type { FC, ReactNode } from "react";

/** The choice of the top level, in place of a program's id. */
export const TOP = "top";

export type Destination = ProgramNode["id"] | typeof TOP;

const Choice: FC<{
  checked: boolean;
  disabled: boolean;
  indent: number;
  icon: ReactNode;
  primary: string;
  secondary?: string;
  tag: string | null;
  bold?: boolean;
  onPick: () => void;
}> = ({ checked, disabled, indent, icon, primary, secondary, tag, bold, onPick }) => (
  <ListItemButton
    role="radio"
    aria-checked={checked}
    selected={checked}
    disabled={disabled}
    onClick={onPick}
    sx={{ pl: 1.5 + indent * 3, borderRadius: "6px" }}
    data-analytics-id="program-move-destination"
  >
    <ListItemIcon sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
    <ListItemText
      primary={primary}
      secondary={secondary}
      slotProps={{
        primary: { noWrap: true, sx: { fontWeight: bold ? 600 : undefined } },
        secondary: { noWrap: true },
      }}
    />
    {tag ? <Chip size="small" label={tag} sx={{ ml: 1, fontSize: 11 }} /> : null}
    {checked ? (
      <RadioButtonChecked fontSize="small" color="primary" sx={{ ml: 1.5 }} />
    ) : (
      <RadioButtonUnchecked fontSize="small" sx={{ ml: 1.5, color: "neutral.400" }} />
    )}
  </ListItemButton>
);

/**
 * The first step of a move: a search and a list of the places that the program can go, from
 * programMoveTargets. The top level comes first.
 */
export const MoveDestinationStep: FC<{
  program: ProgramNode;
  /** The number of folders inside the program. */
  folders: number;
  targets: ProgramMoveTarget[];
  query: string;
  onQuery: (query: string) => void;
  destination: Destination | null;
  onPick: (destination: Destination) => void;
}> = ({ program, folders, targets, query, onQuery, destination, onPick }) => {
  const searching = Boolean(query.trim());

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Pick a program or folder to move it into, or move it to the top level.
        {folders ? ` Its ${plural(folders, "folder")} and their communications move with it.` : ""}
      </Typography>
      <TextField
        autoFocus
        fullWidth
        size="small"
        type="search"
        label="Find a program or folder"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <List role="radiogroup" aria-label="Destination" sx={{ py: 0 }}>
        <Choice
          checked={destination === TOP}
          disabled={!program.parent_id}
          indent={0}
          icon={<VerticalAlignTopIcon fontSize="small" />}
          primary="Top level"
          secondary="Make it a program of its own"
          tag={program.parent_id ? null : "Current location"}
          bold
          onPick={() => onPick(TOP)}
        />
        {targets.map(({ node, depth, path, tag, disabled }) => (
          <Choice
            key={node.id}
            checked={destination === node.id}
            disabled={disabled}
            indent={searching ? 0 : depth}
            icon={
              depth ? (
                <FolderOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              ) : (
                <ProgramIcon
                  data={node.icon_value}
                  fontSize="small"
                  sx={{ color: node.theme_colour }}
                />
              )
            }
            primary={node.title}
            secondary={searching && path ? path : undefined}
            tag={tag}
            bold={!depth}
            onPick={() => onPick(node.id)}
          />
        ))}
      </List>
      {targets.length ? null : (
        <Typography variant="body2" sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
          No programs or folders match that search.
        </Typography>
      )}
    </Stack>
  );
};
